import { Box, CircularProgress, styled, Typography, useTheme } from '@mui/material';
import { useQueryGetCredentials } from './queries';
import { DEFAULT_READ_ERROR_MESSAGE } from 'hooks/errorHandling/errorHandlingHooks';
import { Alert } from 'sharedComponents/Alert';
import { CredentialRow } from './CredentialRow';
import { ATSCredentialsWithVerificationTestIds } from 'data-testids/ATS';

export const CredentialsListWithVerification: React.FC<{
  candidateId: number;
  useLightBorder: boolean;
}> = ({ candidateId, useLightBorder }) => {
  const theme = useTheme();

  const borderColor = useLightBorder ? theme.palette.white.darker : theme.palette.border.main;

  return (
    <Wrapper
      borderColor={borderColor}
      data-testid={ATSCredentialsWithVerificationTestIds.CREDENTIAL_LIST}
    >
      <SectionTitle data-testid={ATSCredentialsWithVerificationTestIds.CREDENTIALS_LIST_TITLE}>
        Credentials
      </SectionTitle>
      <CredentialsListContent candidateId={candidateId} />
    </Wrapper>
  );
};

const CredentialsListContent: React.FC<{ candidateId: number }> = ({ candidateId }) => {
  const { data, isLoading, isError } = useQueryGetCredentials(candidateId);

  if (isLoading)
    return <CircularProgress data-testid={ATSCredentialsWithVerificationTestIds.LOADING_SPINNER} />;

  if (isError)
    return (
      <Alert type="error" dataTestId={ATSCredentialsWithVerificationTestIds.ERROR_ALERT}>
        {DEFAULT_READ_ERROR_MESSAGE}
      </Alert>
    );

  if (data.length === 0)
    return (
      <Alert type="info" dataTestId={ATSCredentialsWithVerificationTestIds.NO_CREDENTIALS_ALERT}>
        No credentials found
      </Alert>
    );

  return (
    <ListContainer>
      {data.map((credential) => (
        <CredentialRow key={credential.id} credential={credential} />
      ))}
    </ListContainer>
  );
};

const Wrapper = styled(Box)(({ theme, borderColor }) => ({
  borderRadius: theme.spacing(0.5),
  padding: theme.spacing(2),
  gap: theme.spacing(2),
  border: `1px solid ${borderColor}`,
}));

const ListContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.h2.fontSize,
  fontWeight: theme.typography.fontWeightRegular,
  color: theme.palette.text.tertiary,
  marginBottom: theme.spacing(2),
}));
